import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Sidebar from './sidebar';
import Modal, { ModalButton } from '../../ui/Modal';

import AddIcon from '@mui/icons-material/Add';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  StatCard, RefreshButton, SearchField,
  StatusBadge, TableCard, LoadingState, EmptyState, Pagination,
} from '../../ui/CrmPage';

const API = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 15;
const fmt = (s) => (s ? new Date(s).toLocaleDateString('ru-RU') : '—');

const TrackSheet = () => {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [eligibleOrders, setEligibleOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}v1/tracksheets`);
      const data = await r.json();
      setSheets(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const loadDetails = async (id) => {
    if (details[id]) { setExpanded(expanded === id ? null : id); return; }
    try {
      const r = await fetch(`${API}v1/tracksheets/${id}`);
      const data = await r.json();
      setDetails((prev) => ({ ...prev, [id]: data }));
      setExpanded(id);
    } catch (e) { console.error(e); }
  };

  const complete = async (id) => {
    if (!window.confirm('Завершить путевой лист?')) return;
    await fetch(`${API}v1/tracksheets/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_completed: true }),
    });
    await load();
  };
  const remove = async (id) => {
    if (!window.confirm('Удалить путевой лист?')) return;
    await fetch(`${API}v1/tracksheets/${id}`, { method: 'DELETE' });
    await load();
  };

  const openCreate = async () => {
    setCreateOpen(true);
    setSelectedOrders([]); setSelectedDriver('');
    try {
      const [driversRes, ordersRes] = await Promise.all([
        fetch(`${API}v1/drivers`).then((r) => r.json()),
        fetch(`${API}v1/orders`).then((r) => r.json()),
      ]);
      setDrivers(Array.isArray(driversRes) ? driversRes : []);
      const eligible = (Array.isArray(ordersRes) ? ordersRes : []).filter(
        (o) => !o.is_deleted && !o.id_track_sheet && Number(o.id_status) !== 13 && Number(o.id_status) !== 3
      );
      setEligibleOrders(eligible);
    } catch (e) { console.error(e); }
  };

  const submitCreate = async () => {
    if (selectedOrders.length === 0) { alert('Выберите хотя бы один заказ.'); return; }
    try {
      const r = await fetch(`${API}v1/tracksheets`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orders: selectedOrders.map((id) => Number(id)),
          id_drivers: selectedDriver ? Number(selectedDriver) : null,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      setCreateOpen(false);
      await load();
    } catch (e) { console.error(e); alert('Ошибка'); }
  };

  const toggleOrder = (id) => {
    setSelectedOrders((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const summary = useMemo(() => ({
    total: sheets.length,
    active: sheets.filter(s => !s.is_completed).length,
    completed: sheets.filter(s => s.is_completed).length,
    orders: sheets.reduce((sum, s) => sum + (Number(s.orders_count) || 0), 0),
  }), [sheets]);

  const filteredSheets = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return sheets.filter(s => {
      if (!term) return true;
      return (
        String(s.id || '').includes(term) ||
        (s.driver_names || '').toLowerCase().includes(term) ||
        (s.vehicle_names || '').toLowerCase().includes(term)
      );
    });
  }, [sheets, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredSheets.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paginatedSheets = filteredSheets.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className="track-sheet-container crmpage">
      <div className="track-sheet-sidebar">
        <Sidebar />
      </div>

      <div className="track-sheet-basic-container crmpage__main">
        <header className="crmpage__head">
          <h1 className="crmpage__title">Путевой лист</h1>
          <div className="crmpage__head-actions">
            <button className="crmpage__btn crmpage__btn--primary" onClick={openCreate}>
              <AddIcon sx={{ fontSize: 18 }} />
              Создать путевой
            </button>
          </div>
        </header>

        <section className="crmpage__stats">
          <StatCard label="Всего"        value={summary.total}     accent="neutral" />
          <StatCard label="В работе"     value={summary.active}    accent="info" />
          <StatCard label="Завершены"    value={summary.completed} accent="success" />
          <StatCard label="Заказов всего" value={summary.orders}    accent="warning" />
          <RefreshButton onClick={() => setReloadKey(k => k + 1)} />
        </section>

        <section className="crmpage__filters">
          <SearchField
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Поиск: номер, экспедитор, транспорт…"
          />
        </section>

        <TableCard>
          {loading ? (
            <LoadingState text="Загружаем путевые…" />
          ) : paginatedSheets.length === 0 ? (
            <EmptyState title="Путевых нет" text="Создайте первый путевой лист для отправки заказов." />
          ) : (
            <>
              <div className="crmpage__table-wrap">
                <table className="crmpage__table">
                  <colgroup>
                    <col style={{ width: 80 }} />
                    <col style={{ width: 110 }} />
                    <col />
                    <col />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 220 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Номер</th>
                      <th className="crmpage__hide-1000">Создан</th>
                      <th>Экспедитор</th>
                      <th className="crmpage__hide-880">Транспорт</th>
                      <th>Статус</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSheets.map((s) => (
                      <React.Fragment key={s.id}>
                        <tr className="crmpage__row" onClick={() => loadDetails(s.id)}>
                          <td><span className="crmpage__num">№{s.id}</span></td>
                          <td className="crmpage__date crmpage__hide-1000">{fmt(s.created_at)}</td>
                          <td>
                            <div className="crmpage__primary">{s.driver_names || '—'}</div>
                            <div className="crmpage__sub">{s.orders_count ?? 0} заказ(ов)</div>
                          </td>
                          <td className="crmpage__muted crmpage__hide-880">{s.vehicle_names || '—'}</td>
                          <td><StatusBadge name={s.is_completed ? 'Завершён' : 'В работе'} /></td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              {!s.is_completed && (
                                <button className="crmpage__btn crmpage__btn--ghost" style={{ height: 28, fontSize: 12, color: '#0F5B3C', background: 'rgba(74,165,135,0.10)', border: 'none' }} onClick={() => complete(s.id)}>
                                  <DoneAllIcon sx={{ fontSize: 16 }} /> Завершить
                                </button>
                              )}
                              <button className="crmpage__btn crmpage__btn--ghost" style={{ height: 28, fontSize: 12, color: '#B53A36', background: 'rgba(235,87,87,0.10)', border: 'none' }} onClick={() => remove(s.id)}>
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                              </button>
                              <button className="crmpage__btn crmpage__btn--ghost" style={{ height: 28, fontSize: 12, padding: '0 8px', border: 'none', background: 'transparent' }} onClick={() => loadDetails(s.id)}>
                                <ExpandMoreIcon sx={{
                                  fontSize: 18,
                                  transform: expanded === s.id ? 'rotate(180deg)' : 'none',
                                  transition: 'transform 200ms ease',
                                  color: 'inherit',
                                }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expanded === s.id && details[s.id] && (
                          <tr>
                            <td colSpan={6} style={{ padding: '14px 18px', background: '#FBF8F3' }}>
                              <table className="crmpage__table" style={{ background: 'transparent' }}>
                                <thead>
                                  <tr>
                                    <th style={{ background: '#FBF8F3', borderBottom: '1px solid var(--border)' }}>№</th>
                                    <th style={{ background: '#FBF8F3', borderBottom: '1px solid var(--border)' }}>Клиент</th>
                                    <th style={{ background: '#FBF8F3', borderBottom: '1px solid var(--border)' }}>Адрес</th>
                                    <th style={{ background: '#FBF8F3', borderBottom: '1px solid var(--border)' }}>Доставка</th>
                                    <th style={{ background: '#FBF8F3', borderBottom: '1px solid var(--border)' }}>Статус</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(details[s.id].orders || []).map((o) => (
                                    <tr key={o.id}>
                                      <td><span className="crmpage__num">№{o.order_number}</span></td>
                                      <td>{o.client_name}</td>
                                      <td className="crmpage__muted">{o.delivery_address}</td>
                                      <td className="crmpage__date">{fmt(o.delivery_date)}</td>
                                      <td><StatusBadge name={o.status_name} /></td>
                                    </tr>
                                  ))}
                                  {(details[s.id].orders || []).length === 0 && (
                                    <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--fg-muted)', padding: '16px' }}>Заказы не привязаны</td></tr>
                                  )}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
            </>
          )}
        </TableCard>
      </div>

      {/* Create modal */}
      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Новый путевой лист"
        subtitle="Выберите экспедитора и заказы для включения"
        size="lg"
        footer={
          <>
            <ModalButton variant="ghost" onClick={() => setCreateOpen(false)}>Отмена</ModalButton>
            <ModalButton variant="primary" onClick={submitCreate} disabled={!selectedOrders.length}>
              Создать ({selectedOrders.length})
            </ModalButton>
          </>
        }
      >
        <div className="uimodal-field" style={{ gridTemplateColumns: '1fr' }}>
          <span className="uimodal-field__label">Экспедитор</span>
          <select
            value={selectedDriver}
            onChange={(e) => setSelectedDriver(e.target.value)}
            style={{
              width: '100%', height: 40, padding: '0 12px',
              border: '1px solid rgba(56,47,42,0.18)', borderRadius: 8,
              fontFamily: 'inherit', fontSize: 13.5, background: '#fff', outline: 'none',
            }}
          >
            <option value="">— не назначен —</option>
            {drivers.map((d) => (<option key={d.id} value={d.id}>{d.full_name}</option>))}
          </select>
        </div>
        <div style={{ margin: '16px 0 8px', fontWeight: 600, fontSize: 13 }}>
          Заказы ({selectedOrders.length} выбрано)
        </div>
        <table className="uimodal-table">
          <thead>
            <tr><th></th><th>№</th><th>Клиент</th><th>Доставка</th></tr>
          </thead>
          <tbody>
            {eligibleOrders.map((o) => (
              <tr key={o.id}>
                <td style={{ width: 30 }}>
                  <input type="checkbox" className="crmpage__check"
                    checked={selectedOrders.includes(o.id)}
                    onChange={() => toggleOrder(o.id)} />
                </td>
                <td><span className="crmpage__num">№{o.order_number}</span></td>
                <td>{o.client_name || o.id_client}</td>
                <td className="crmpage__date">{fmt(o.delivery_date)}</td>
              </tr>
            ))}
            {eligibleOrders.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 16, color: 'var(--fg-muted)' }}>Нет доступных заказов</td></tr>
            )}
          </tbody>
        </table>
      </Modal>
    </div>
  );
};

export default TrackSheet;
